import { ActionType } from '../../core/redux/actionType';
import { makeApiAction, makeRsaaTypes, ApiReducer, makeApiReducer } from '../../core/api/rsaa';
import { Endpoints } from '../../core/api/endpoints';
import { getLoading, addLoading, addError, noLoadingNoError } from '../../core/api/module';
import { SendTo } from '../../project/survey/raters/models';


const KEY_ACTION = ActionType.SendRaterApprovalRequest;
const ACTIONS    = [
  ActionType.SendRaterApprovalRequest,
  ActionType.SendRaterApprovalResponse,
  ActionType.SendRaterApprovalError,
];


type Payload = string;


type Meta = {
  approverId: number;
  participantId: number;
  projectId: number;
};


/**
 * Dispatch this action to request approval for the participant's added raters from the designated approver
 *
 * @param approverId - the person Id of the rater designated as approver for this list of raters
 * @param participantId - the specific project participant for whom we wish to invite raters
 * @param projectId - the project to which we wish to invite raters
 * @param comment - a custom comment included in the email sent out to invited raters
 * @param to - the list of rater email addresses to invite (along with the database Id for the language to be used for
 * each message)
 * @param ratersApproved - the number of raters approved by the designated approver
 * @param ratersDeclined - the number of raters declined by the designated approver
 * @param ratersAdded - the number of raters added to the project by the designated approver (as opposed to the
 * participant themself)
 */
export const sendRaterApprovalAction = (
  approverId: number,
  participantId: number,
  projectId: number,
  comment: string,
  to: SendTo[],
  ratersApproved: number,
  ratersDeclined: number,
  ratersAdded: number,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.SendRaterApproval,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify({
      projectId,
      participantId,
      comment,
      to,
      approverId,
      ratersApproved,
      ratersDeclined,
      ratersAdded,
    }),
    types:    makeRsaaTypes<Payload, Meta>(ACTIONS, { approverId, participantId, projectId }),
    bailout:  state => !!getLoading(state, KEY_ACTION, `${approverId}-${participantId}-${projectId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const approverId = action.meta.approverId;
  const participantId = action.meta.participantId;
  const projectId = action.meta.projectId;
  const metaTag = `${approverId}-${participantId}-${projectId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  return noLoadingNoError(state, KEY_ACTION, metaTag);
};

export const sendRaterApprovalReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
